import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Divider } from '@mui/material';
import type { CellValueChangedEvent } from 'ag-grid-community';
import AccordionPanel from '@/components/UI/AccordionPanel/AccordionPanel';
import Button from '@/components/UI/Button/Button';
import ConfirmDialog from '@/components/UI/Dialog/ConfirmDialog';
import MultipleButtons from '@/components/UI/MultipleButtons/MultipleButtons';
import SimpleButton from '@/components/UI/SimpleButton/SimpleButton';
import Spinner from '@/components/UI/Spinner/Spinner';
import { CELL_EDITOR_TYPES, CELL_RENDERER_TYPES, FILTER_TYPES } from '@/components/UI/TableListEditable/TableListEditableConstants';
import TableListEditableNew from '@/components/UI/TableListEditable/TableListEditableNew';
import useGlobalSpinnerContext from '@/hooks/useGlobalSpinner';
import useToastContext from '@/hooks/useToastContext';
import administrationServices from '@/services/administrationServices';
import titleService from '@/services/titleService';
import { booleanCellRenderer, ModelSerieAdmin as ModelSerie, ModelSeriesState, Plant } from '@/types';
import { TreeStatusSelectorParams, TreeStatusSelectorOptions, ModelSeriesGridRow } from '@/types/ModelSeries';
import CreateEditModelSeriesPopup from './CreateEditModelSeriesPopup';
import {
  chipFilterTypes, emptyStateMS, filteringStatesModelSeriesView,
  filteringStatesPlantsView,
} from './modelSeries-constants';
import { generateModelSeriesState, getTreeStatusSummaryValues } from './modelSeries-utils';
import styles from './ModelSeries.module.scss';

export const [MS_VIEW, PLANTS_VIEW] = ['modelSeriesView', 'plantsView'];

const MODEL_SERIES_COLUMNS_NAMES = {
  MODEL_SERIES: 'modelSeries',
  TYPE_VERSION: 'typeVersion',
  DIALOG: 'dialog',
  ACTIVE_FOR_EFA: 'activeForEfa',
  PLANTS: 'plants',
  TYPE: 'type',

};
const PLANTS_COLUMNS_NAMES = {
  PLANT: 'plant',
  MODEL_SERIES: 'modelSeries',
  TYPE_VERSION: 'typeVersion',
  DIALOG: 'dialog',
  ACTIVE_FOR_EFA: 'activeForEfa',
};

const ModelSeries: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language.toUpperCase();
  const [state, setState] = useState<ModelSeriesState>(emptyStateMS);
  const [selection, setSelection] = useState<string[]>([]);
  const [visibleTable, setVisibleTable] = useState(MS_VIEW);
  const [filterSelected, setFilterSelected] = useState(chipFilterTypes[0]);
  const [isLoading, setIsLoading] = useState(false);
  const [popupOpen, setPopupOpen] = useState('');
  const { addToast } = useToastContext();
  const { setLoading } = useGlobalSpinnerContext();
  const currentTableData: any = state[visibleTable];
  const isModelSeriesView = visibleTable === MS_VIEW;
  const currentSelection: any[] = useMemo(() => selection?.map((rowID) => currentTableData.allModelSeries
    .find((ms) => ms.idGenerated === rowID)), [selection, currentTableData.allModelSeries]);

  const buildTreeBooleanStatusSelector = useCallback((options?: TreeStatusSelectorOptions) => {
    const { hideChildWhenTrue = true } = options ?? {};

    return ({ data, value }: TreeStatusSelectorParams) => {
      if (data?.isChild) {
        return { component: () => hideChildWhenTrue && value ? null : booleanCellRenderer({ value: false }) };
      }

      const { parentValue, active, total } = getTreeStatusSummaryValues(value);

      return {
        component: () => (
          <span className={styles.BooleanCell}>
            {booleanCellRenderer({ value: active === total })}
            <span>{parentValue ? `${active}/${total}` : '-'}</span>
          </span>
        ),
      };
    };
  },
  [],
  );

  const buildActiveInEfaCellRenderer = useCallback(() => {
    return ({ data, value }: TreeStatusSelectorParams) => {
      if (data?.isChild) {
        return { component: CELL_RENDERER_TYPES.CHECKBOX };
      }
      const { parentValue, active, total } = getTreeStatusSummaryValues(value);

      return {
        component: () => (
          <span className={styles.BooleanCell}>
            <span>{parentValue ? `${active}/${total}` : '-'}</span>
          </span>
        ),
      };
    };
  }, []);

  const modelSeriesColumns = useMemo(() => [
    { name: MODEL_SERIES_COLUMNS_NAMES.MODEL_SERIES, title: t('software.modelSeries') },
    { name: MODEL_SERIES_COLUMNS_NAMES.TYPE_VERSION, title: t('administration.typeVersion'), flex: 0.5 },
    { name: MODEL_SERIES_COLUMNS_NAMES.DIALOG,
      title: t('administration.msSection.dialogStatus'),
      filterType: FILTER_TYPES.SET,
      flex: 0.5,
      cellRendererSelector: buildTreeBooleanStatusSelector(),
    },
    {
      name: MODEL_SERIES_COLUMNS_NAMES.ACTIVE_FOR_EFA,
      title: t('administration.msSection.activeInEfa'),
      editable: (params: { data?: { isChild?: boolean } }) => Boolean(params.data?.isChild),
      flex: 0.5,
      cellEditor: CELL_EDITOR_TYPES.CHECKBOX,
      cellRendererSelector: buildActiveInEfaCellRenderer(),
    },
    { name: MODEL_SERIES_COLUMNS_NAMES.PLANTS, title: t('administration.plants'), wrapText: true, autoHeight: true },
    { name: MODEL_SERIES_COLUMNS_NAMES.TYPE, title: t('administration.type'), flex: 0.5 },
  ], [t, buildTreeBooleanStatusSelector, buildActiveInEfaCellRenderer]);

  const plantsColumns = useMemo(() => [
    { name: PLANTS_COLUMNS_NAMES.PLANT, title: t('plant') },
    { name: PLANTS_COLUMNS_NAMES.MODEL_SERIES, title: t('software.modelSeries') },
    { name: PLANTS_COLUMNS_NAMES.TYPE_VERSION, title: t('administration.typeVersion') },
    { name: PLANTS_COLUMNS_NAMES.DIALOG,
      title: t('administration.msSection.dialogStatus'),
      flex: 0.5,
      cellRendererSelector: buildTreeBooleanStatusSelector(),
    },

    {
      name: PLANTS_COLUMNS_NAMES.ACTIVE_FOR_EFA,
      title: t('administration.msSection.activeInEfa'),
      flex: 0.5,
      cellRendererSelector: buildTreeBooleanStatusSelector(),
    },
  ], [t, buildTreeBooleanStatusSelector]);

  const currentColumns = useMemo(() => (isModelSeriesView ? modelSeriesColumns : plantsColumns), [isModelSeriesView, modelSeriesColumns, plantsColumns]);

  useEffect(() => {
    titleService.updatePageTitle(t('dashboard.administration'), t('software.modelSeries'));
  }, [t, lang]);

  const getPlantsModelSeries = () => {
    setIsLoading(true);
    Promise.all([
      administrationServices.getPlants(),
      administrationServices.getModelSeries(),
    ]).then((res) => {
      const [resPlants, resModelSeries] = res;
      const plantsActive = resPlants.plants.filter((plant: Plant) => plant.active);
      const stateGenerated = generateModelSeriesState(resModelSeries.modelSeriesList, plantsActive);

      setVisibleTable(MS_VIEW);
      setState(stateGenerated);
    })
      .catch((error) => addToast(error.message, 'ERROR'))
      .finally(() => {
        setSelection([]);
        setIsLoading(false);
      });
  };

  const handleDeleteModelSeries = () => {
    setLoading(true);
    const modelSeriesSelected = currentSelection.reduce((acc, curr) => acc
      .concat(curr?.children.map((tp) => ({ modelSeries: tp.modelSeriesRaw, typeVersion: tp.typeVersion }))), []);

    administrationServices.deleteModelSeries(modelSeriesSelected)
      .then(() => { getPlantsModelSeries(); })
      .catch((error) => addToast(error.message, 'ERROR'))
      .finally(() => {
        setPopupOpen('');
        setLoading(false);
      });
  };

  const handleEditActiveInEFACol = useCallback((event: CellValueChangedEvent<ModelSeriesGridRow>) => {
    const { colDef: { field }, data, newValue, oldValue } = event;

    if (!field || !data || newValue === oldValue) return;

    // Only process activeForEfa in children rows
    if (field !== MODEL_SERIES_COLUMNS_NAMES.ACTIVE_FOR_EFA) return;
    if (!data.isChild) return;
    if (typeof newValue !== 'boolean') return;

    const { modelSeriesRaw, typeVersion } = data;
    if (!modelSeriesRaw || !typeVersion) return;

    const payload = {
      modelSeries: modelSeriesRaw,
      activeForEfa: newValue,
      typeVersion,
    };

    const newModelSeriesRaw: ModelSerie[] = state.allModelSeriesRaw.map((ms) => (
      ms.modelSeries === modelSeriesRaw
        ? {
          ...ms,
          typeVersions: ms.typeVersions.map((typeVersionItem) => (
            typeVersionItem.code === typeVersion ? { ...typeVersionItem, activeForEfa: newValue } : typeVersionItem
          )),
        }
        : ms
    ));

    const stateGenerated = generateModelSeriesState(
      newModelSeriesRaw,
      state.plantsActive,
      currentTableData.allModelSeries,
    );
    setState(stateGenerated);

    administrationServices.activeModelSerieForEfa(payload)
      .catch((error) => addToast(error.message, 'ERROR'));
  }, [state, currentTableData, addToast]);

  const handleFilterClick = (newFilter: string) => {
    setSelection([]);
    setFilterSelected(newFilter);
  };

  const onTableModeChange = (mode: string) => {
    setVisibleTable(mode);
    handleFilterClick(chipFilterTypes[0]);
  };

  const onCreateModelSeries = (refresh: boolean) => {
    setPopupOpen('');

    if (refresh) getPlantsModelSeries();
  };

  useEffect(() => {
    getPlantsModelSeries();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <>
      {isLoading && <Spinner isVisible isMask />}
      {!isLoading && (
        <>
          <AccordionPanel
            title={`${t('administration.msSection.availableModelSeriesInSystem')} ${currentTableData.allModelSeries ? `(${currentTableData.allModelSeries.length})` : ''}`}
          >
            <div className={styles.ChipFiltersContainer}>
              {chipFilterTypes.map((filterName) => (
                <Button
                  key={`filter-${filterName}`}
                  id={`filter-${filterName}`}
                  handleClick={() => handleFilterClick(filterName)}
                  buttonStyle="GreyFilter"
                  btnText={t(`administration.msSection.${(filterName === 'noPlantAssigment' && !isModelSeriesView) ? 'notAssigned' : filterName}`)}
                  active={filterSelected === filterName}
                />
              ))}
            </div>
            <Divider />
            <div className={styles.ButtonsTable}>
              <div className={styles.ButtonsContentTableLeft}>
                <MultipleButtons
                  buttons={[{
                    label: t('administration.msSection.modelSeriesView'),
                    id: MS_VIEW,
                    checked: visibleTable === MS_VIEW,
                  },
                  {
                    label: t('administration.msSection.plantsView'),
                    id: PLANTS_VIEW,
                    checked: visibleTable === PLANTS_VIEW,
                  }]}
                  onClickAction={onTableModeChange}
                />
              </div>
              <div className={styles.ButtonsContentTableRight}>
                {isModelSeriesView && (
                  <SimpleButton
                    id="btn-add-modelseries"
                    color="primary"
                    buttonText={t('administration.msSection.addMS')}
                    handleClick={() => setPopupOpen('CREATION')}
                  />
                )}
                <SimpleButton
                  id="btn-edit-modelseries"
                  color="primary"
                  buttonText={t('administration.msSection.editMS')}
                  handleClick={() => setPopupOpen('EDITION')}
                  disabled={selection.length === 0}
                />
                {isModelSeriesView && (
                  <SimpleButton
                    id="btn-remove-modelseries"
                    color="primary"
                    buttonText={t('administration.msSection.removeMS')}
                    handleClick={() => setPopupOpen('DELETION')}
                    disabled={selection.length === 0}
                  />
                )}
              </div>
            </div>
            <div className="w-100">
              <TableListEditableNew
                columnId="idGenerated"
                isFiltering
                filteringStateColumns={isModelSeriesView ? filteringStatesModelSeriesView : filteringStatesPlantsView}
                hasCheckbox
                selection={selection}
                onSelectionChange={(rows) => setSelection(rows as string[])}
                list={currentTableData[filterSelected]}
                columns={currentColumns}
                onCellValueChanged={handleEditActiveInEFACol}
                loading={isLoading}
                hasMinWidth
                treeDataConfig={{
                  childrenProperty: 'children',
                  columnExpanded: isModelSeriesView ? 'modelSeries' : 'plant',
                  childrenNotSelectable: true,
                }}
              />
            </div>
          </AccordionPanel>

          {popupOpen === 'DELETION' && (
            <ConfirmDialog
              reject={() => setPopupOpen('')}
              confirm={handleDeleteModelSeries}
              title={t('dialog.deleteModelSeries.title')}
              closeBtnText={t('dialog.cancel')}
              confirmBtnText={t('dialog.remove')}
            >
              <p className={styles.RemoveMSMessage}>{t('dialog.deleteModelSeries.message')}</p>
              {currentSelection.map((ms) => <div key={ms.modelSeries}><p className={styles.MSText}><b>{ms.modelSeries}</b></p><p>{ms.typeVersion}</p></div>)}
            </ConfirmDialog>
          )}
          {['EDITION', 'CREATION'].includes(popupOpen) && (
            <CreateEditModelSeriesPopup
              editionMode={popupOpen === 'EDITION'}
              modelSeriesSelected={currentSelection}
              plantsSelected={currentSelection}
              currentTableData={currentTableData[filterSelected]}
              plants={state.plantsActive}
              tableView={visibleTable}
              confirmChanges={onCreateModelSeries}
              allModelSeries={state.allModelSeriesRaw}
            />
          )}
        </>
      )}
    </>
  );
};

export default ModelSeries;
